import { create } from 'zustand';
import { api } from '../lib/api/client';
import type { Book, User, Subscription, Progress } from '../lib/api/types';

/**
 * 双模式共享状态层（Phase 2 Week 8 · Ticket 01）
 * 管理所有模式共用的全局只读数据：书库、当前书籍、用户、订阅、阅读进度。
 * 加载状态按操作区分（booksLoading/bookLoading），避免并发冲突。
 */

interface SharedState {
  // ── 全局只读数据 ──
  books: Book[];
  currentBook: Book | null;
  user: User | null;
  subscription: Subscription | null;
  progressList: Progress[];

  // ── 加载状态（按操作区分） ──
  booksLoading: boolean;
  booksError: string | null;
  bookLoading: boolean;
  bookError: string | null;

  // ── Actions ──
  loadBooks: () => Promise<void>;
  loadBook: (slug: string) => Promise<void>;
  setUser: (user: User | null) => void;
  setSubscription: (sub: Subscription | null) => void;
  setProgressList: (progress: Progress[]) => void;
}

export const useSharedStore = create<SharedState>((set, get) => ({
  books: [],
  currentBook: null,
  user: null,
  subscription: null,
  progressList: [],
  booksLoading: false,
  booksError: null,
  bookLoading: false,
  bookError: null,

  loadBooks: async () => {
    if (get().booksLoading) return; // 防并发重复加载
    set({ booksLoading: true, booksError: null });
    try {
      const data = await api.books.list();
      set({ books: data.books ?? [], booksLoading: false });
    } catch (err: any) {
      set({ booksError: err.message ?? '书库加载失败', booksLoading: false });
    }
  },

  loadBook: async (slug: string) => {
    if (get().bookLoading) return; // 防并发重复加载
    set({ bookLoading: true, bookError: null });
    try {
      const data = await api.books.get(slug);
      set({ currentBook: data, bookLoading: false });
    } catch (err: any) {
      set({ bookError: err.message ?? '绘本详情加载失败', bookLoading: false });
    }
  },

  setUser: (user) => set({ user }),
  setSubscription: (sub) => set({ subscription: sub }),
  setProgressList: (progress) => set({ progressList: progress }),
}));

/** 非 React 环境直接取 store state */
export function getSharedStore() {
  return useSharedStore.getState();
}
